import React from 'react';
import Assets from '../../constants/images';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  ChatAgentIcon,
  ChatsIconEmoji,
  CompanyManagementIcon,
  ContactSupportIcon,
  DashboardIcon,
  LogoutIcon,
  ReportManagementIcon,
  SessionIntelligenceIcon,
  SettingIcon,
} from '../../constants/svgs';
import { link } from '../../constants/appConstants';
import { Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useSignoutMutation } from '../../store/apis/authApi';
import { emptyLocalStorage, getRefreshTokenFromLocalStorage } from '../../utils/helper';
import { successMsg } from '../../constants/msgs';
import { persistor } from '../../store/store';
import { useSelector } from 'react-redux';
import { getAuthUser } from '../../store/slices/authSlice';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { useState, useRef, useEffect } from 'react';

const DashboardSidebar = ({ isActive, handleSidebar }) => {
  const [signout, { isLoading }] = useSignoutMutation();
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const authUser = useSelector(getAuthUser);
  const isSuper = authUser?.role_id === 4;
  const location = useLocation();
  const [isSettingsOpen, setIsSettingsOpen] = useState(
    location.pathname === link.settings || location.pathname === link.changePassword
  );
  const [isLiveChatOpen, setIsLiveChatOpen] = useState(
    location.pathname === link.livechatTickets ||
      location.pathname === link.livechatSessions ||
      location.pathname === link.livechatAuditLogs ||
      location.pathname === link.livechatContacts
  );

  React.useEffect(() => {
    if (location.pathname === link.settings || location.pathname === link.changePassword) {
      setIsSettingsOpen(true);
    }
    if (
      location.pathname === link.livechatTickets ||
      location.pathname === link.livechatSessions ||
      location.pathname === link.livechatAuditLogs ||
      location.pathname === link.livechatContacts
    ) {
      setIsLiveChatOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isSettingsOpen && menuRef.current) {
      setTimeout(() => {
        menuRef.current.scrollTo({
          top: menuRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }, 100);
    }
  }, [isSettingsOpen]);

  const handleSignout = async () => {
    // Revoke the session at central auth (send the refresh token); best-effort —
    // always clear locally + navigate even if the upstream call fails.
    const refresh = getRefreshTokenFromLocalStorage();
    const { data } = await signout({ refresh });
    persistor.purge();
    emptyLocalStorage();
    navigate(link.default);
    successMsg(data?.message || 'Logged out');
  };

  return (
    <div
      className={`dashboard-sidebar pt-9 pb-9 flex flex-col h-full ${isActive ? 'active' : ''} `}
    >
      <div className="mt-3 pb-5 flex items-center justify-between sidebar-border">
        <img className="ms-9" width={140} src={Assets.DashboardLogo} alt="" />
        <CloseOutlined
          className="hidden relative right-5 cursor-pointer times-icon"
          onClick={handleSidebar}
        />
      </div>

      <ul className="ps-9" ref={menuRef}>
        <li className="mb-6">
          <NavLink className="sidebar-links" to={link.dashboard} onClick={handleSidebar}>
            <div className="me-4">
              <DashboardIcon className="icon" />
            </div>
            Dashboard
          </NavLink>
        </li>

        {isSuper && (
          <li className="mb-6">
            <NavLink
              className={`sidebar-links ${
                location.pathname.startsWith(link.userSessions) ? 'active' : ''
              }`}
              to={link.sessionIntelligence}
              onClick={handleSidebar}
            >
              <div className="me-4">
                <SessionIntelligenceIcon className="icon" />
              </div>
              Session Intelligence
            </NavLink>
          </li>
        )}

        <li className="mb-6">
          <NavLink className="sidebar-links" to={link.reportManagement} onClick={handleSidebar}>
            <div className="me-4">
              <ReportManagementIcon className="icon" />
            </div>
            Reports
          </NavLink>
        </li>

        <li className="mb-6">
          <NavLink className="sidebar-links" to={link.unifiedBrands} onClick={handleSidebar}>
            <div className="me-4">
              <CompanyManagementIcon className="icon" />
            </div>
            Brands
          </NavLink>
        </li>

        <li className="mb-6">
          <NavLink className="sidebar-links" to={link.siteGates} onClick={handleSidebar}>
            <div className="me-4">
              <CompanyManagementIcon className="icon" />
            </div>
            Site Gates
          </NavLink>
        </li>

        <li className="mb-6">
          <NavLink className="sidebar-links" to={link.unifiedUsers} onClick={handleSidebar}>
            <div className="me-4">
              <CompanyManagementIcon className="icon" />
            </div>
            Users
          </NavLink>
        </li>

        <li className="mb-6">
          <NavLink className="sidebar-links" to={link.roles} onClick={handleSidebar}>
            <div className="me-4">
              <ChatAgentIcon className="icon" />
            </div>
            Roles
          </NavLink>
        </li>

        {isSuper && (
          <li className="mb-6">
            <NavLink className="sidebar-links" to={link.chatManagement} onClick={handleSidebar}>
              <div className="me-4">
                <ChatsIconEmoji className="icon" />
              </div>
              Chats
            </NavLink>
          </li>
        )}
        <li className="mb-6">
          <NavLink className="sidebar-links" to={link.contactSupport} onClick={handleSidebar}>
            <div className="me-4">
              <ContactSupportIcon className="icon" />
            </div>
            Contact Support
          </NavLink>
        </li>
        <li className="mb-6">
          <div
            className={`sidebar-links cursor-pointer flex justify-between items-center ${
              isLiveChatOpen ? 'active' : ''
            }`}
            onClick={() => setIsLiveChatOpen(!isLiveChatOpen)}
          >
            <div className="flex items-center">
              <div className="me-4">
                <ChatAgentIcon className="icon" />
              </div>
              LiveChat
            </div>
            {isLiveChatOpen ? (
              <CaretUpOutlined style={{ fontSize: '12px' }} />
            ) : (
              <CaretDownOutlined style={{ fontSize: '12px' }} />
            )}
          </div>

          {isLiveChatOpen && (
            <ul className="ps-8 mt-4">
              <li className="mb-4">
                <NavLink
                  className="sidebar-links"
                  to={link.livechatTickets}
                  onClick={handleSidebar}
                >
                  Tickets
                </NavLink>
              </li>
              <li className="mb-4">
                <NavLink
                  className="sidebar-links"
                  to={link.livechatSessions}
                  onClick={handleSidebar}
                >
                  Chats
                </NavLink>
              </li>
              <li className="mb-4">
                <NavLink
                  className="sidebar-links"
                  to={link.livechatAuditLogs}
                  onClick={handleSidebar}
                >
                  Logs
                </NavLink>
              </li>
              <li className="">
                <NavLink
                  className="sidebar-links"
                  to={link.livechatContacts}
                  onClick={handleSidebar}
                >
                  Contact Us
                </NavLink>
              </li>
            </ul>
          )}
        </li>

        {isSuper && (
          <li className="mb-6">
            <div
              className={`sidebar-links cursor-pointer flex justify-between items-center ${
                isSettingsOpen ? 'active' : ''
              }`}
              onClick={() => setIsSettingsOpen(!isSettingsOpen)}
            >
              <div className="flex items-center">
                <div className="me-4">
                  <SettingIcon className="icon" />
                </div>
                Settings
              </div>
              {isSettingsOpen ? (
                <CaretUpOutlined style={{ fontSize: '12px' }} />
              ) : (
                <CaretDownOutlined style={{ fontSize: '12px' }} />
              )}
            </div>

            {isSettingsOpen && (
              <ul className="ps-8 mt-4">
                <li className="mb-4">
                  <NavLink
                    className="sidebar-links"
                    to={link.subAdminManagement}
                    onClick={handleSidebar}
                  >
                    Sub Admin
                  </NavLink>
                </li>
                <li className="mt-4">
                  <NavLink
                    className="sidebar-links"
                    to={link.changePassword}
                    onClick={handleSidebar}
                  >
                    Change Password
                  </NavLink>
                </li>
              </ul>
            )}
          </li>
        )}
      </ul>

      <div className="mt-6 px-8">
        <Button
          onClick={handleSignout}
          loading={isLoading}
          className="theme-button w-full flex justify-between px-7"
        >
          Logout
          <span>
            <LogoutIcon />
          </span>
        </Button>
      </div>
    </div>
  );
};

export default DashboardSidebar;
